import React, { useState, useEffect } from "react";
import {
  DetailFields,
  JobListColumn,
  OtherPages,
  PageTitleComp,
  ResultWrap1,
  RightLayoutContainer,
} from "../../styledElements";
import { Button, Layout, PageHeader, Tabs } from "antd";
import Iconmoon from "../../../Components/IconsFiles/icon";
import HeaderNav from "../../../Components/Header/Header";
import InfiniteScroll from "react-infinite-scroll-component";
import { editAllNotification, editNotification, getAllNotifications } from "../../../Services/notifications&rolesServices";
import { syncUnreadNotificationCount } from "../../../Services/webSocketService";
import { useNavigate } from "react-router";

const { TabPane } = Tabs;

export default function AllNotification() {
  const [activeTab, setActiveTab] = useState("1");
  const [unreadData, setUnreadData] = useState([]);
  const [earlierData, setEarlierData] = useState([]);
  const [unreadPage, setUnreadPage] = useState(0);
  const [earlierPage, setEarlierPage] = useState(0);
  const [hasMoreUnread, setHasMoreUnread] = useState(true);
  const [hasMoreEarlier, setHasMoreEarlier] = useState(true);
  const pageSize = 6;
  const mainPage = {
    "Deckle Management":"/deckle-matching/job-cards/",
    "Order Management":"/order-entry/order-records/",
    "Configurations":"/configuration/global-settings/"
  }
  const navigate = useNavigate();

  // ✅ Map active tab to type param
  const getType = () => {
    if (activeTab === "1") return "alert";
    if (activeTab === "2") return "event";
    if (activeTab === "3") return null; // All notifications - no type filter
    return null;
  };

  // ✅ Fetch notifications for unread or earlier list
  const fetchNotifications = async (isUnread = true, page = 0,isAll=false) => {
    const start = page * pageSize;
    const end = start + pageSize;
    const type = getType();

    try {
      const res = await getAllNotifications(start, end, type);
      
      const newData = res?.notifications || [];
      
      // Debug: Check data structure
      // if (newData.length > 0) {
      //   console.log("🔍 Sample notification structure:", newData[0]);
      //   console.log("🔍 All read_status values:", newData.map(n => ({ id: n.id, read_status: n.read_status, type: n.type })));
      // }

      if (newData.length < pageSize) {
        if (isUnread || isAll) {
          setHasMoreUnread(false);
        }
        if (!isUnread || isAll) {
          setHasMoreEarlier(false);
        }
      }

      if (isUnread) {
        const unreadFiltered = newData.filter((n) => !n.read_status);
        setUnreadData((prev) => [
          ...prev,
          ...unreadFiltered,
        ]);
        if(unreadFiltered.length == 0)
          setHasMoreUnread(false);
      } else if(isAll){
        const unreadFiltered = newData.filter((n) => !n.read_status);
        const readFiltered = newData.filter((n) => n.read_status);
        
        setUnreadData((prev) => [
          ...prev,
          ...unreadFiltered,
        ]);
        setEarlierData((prev) => [
          ...prev,
          ...readFiltered,
        ]);
        if(unreadFiltered.length == 0)
          setHasMoreUnread(false);
        
        // If no read notifications found, set hasMoreEarlier to false
        if (readFiltered.length === 0) {
          setHasMoreEarlier(false);
        }
      } 
      else {
        const readFiltered = newData.filter((n) => n.read_status);
        setEarlierData((prev) => [
          ...prev,
          ...readFiltered,
        ]);
        
        // If no read notifications found, set hasMoreEarlier to false
        if (readFiltered.length === 0) {
          setHasMoreEarlier(false);
        }
      }
    } catch (err) {
      console.log("❌ Error fetching notifications:", err);
    }
  };

  // ✅ On tab change → reset + refetch
  useEffect(() => {
    // setUnreadPage(0);
    // setEarlierPage(0);
    // setUnreadData([]);
    // setEarlierData([]);
    // setHasMoreUnread(true);
    // setHasMoreEarlier(true);
    fetchNotifications(false, 0,true);
    // fetchNotifications(false, 0);
  }, [activeTab]);

  // ✅ Check if we should disable hasMore for read sections
  useEffect(() => {
    // If we have data but no read notifications, disable hasMoreEarlier
    if (unreadData.length > 0 && earlierData.length === 0 && !hasMoreUnread) {
      setHasMoreEarlier(false);
    }
  }, [unreadData, earlierData, hasMoreUnread]);

  // Debug state changes
  useEffect(() => {
    // If unread data is empty and we're not loading, set hasMore to false
    if (unreadData.length === 0 && !hasMoreUnread) {
      // console.log("📊 No unread data, setting hasMoreUnread to false");
    }
  }, [unreadData, hasMoreUnread]);

  useEffect(() => {
    // If earlier data is empty and we're not loading, set hasMore to false
    if (earlierData.length === 0 && !hasMoreEarlier) {
      // console.log("📊 No earlier data, setting hasMoreEarlier to false");
    }
  }, [earlierData, hasMoreEarlier]);

  // ✅ Load more unread
  const loadMoreUnread = () => {
    const nextPage = unreadPage + 1;
    setUnreadPage(nextPage);
    fetchNotifications(true, nextPage);
  };

  // ✅ Load more earlier
  const loadMoreEarlier = () => {
    const nextPage = earlierPage + 1;
    setEarlierPage(nextPage);
    fetchNotifications(false, nextPage);
  };

  // ✅ Mark one as read
  const markAsRead = (id) => {
    editNotification(id, { read_status: true })
      .then(() => {
        const notif = unreadData.find((item) => item.id === id);
        if (!notif) return;

        notif.read_status = true;
        setUnreadData((prev) => prev.filter((item) => item.id !== id));
        setEarlierData((prev) => [notif, ...prev]);
        syncUnreadNotificationCount(Math.max(unreadData.length - 1, 0));
      })
      .catch((err) => {
        console.log("Error marking notification as read:", err);
      });
  };

  // ✅ Mark all as read
  const markAllAsRead = () => {
    editAllNotification().then(()=>{

      const updated = unreadData.map((n) => ({ ...n, read_status: true }));
      setUnreadData([]);
      setHasMoreUnread(false);
      setEarlierData((prev) => [...updated, ...prev]);
      syncUnreadNotificationCount(0);
    });
  };

  const handleView = (value) => {
    if (value?.jobcard_details) {
      const { jobcard_id, type, process_type } = value.jobcard_details;
      if (type === "multi_stage" || type === "multistage") {
        navigate("/deckle-matching/job-card-details-multistage", { state: { id: jobcard_id, jobcardType: type } });
      } else if (process_type === "simplex" || process_type === "duplex") {
        navigate("/deckle-matching/sheet-job-card-details", { state: { id: jobcard_id, type: 'deckle', cutter_type: process_type, jobcardType: type } });
      } else {
        navigate("/deckle-matching/job-card-details", { state: { id: jobcard_id, jobcardType: type } });
      }
    } else {
      navigate(mainPage[value?.module_name]);
    }
  };

  // ✅ Single notification card
  const messageBox = (value) => (
    <div
      key={value.id}
      style={{
        display: "flex",
        alignItems: "self-start",
        // width: "100%",
        height: "97px",
        borderLeft: value.read_status
          ? "4px solid #F3F2F1"
          : value.type === "alert"
          ? "4px solid #EA655B"
          : "4px solid #0268D9",
        backgroundColor: value.read_status ? "white" : "#FAF9F8",
        borderRadius: "5px",
        margin: "0.8% 0%",
        padding: "0.8%",
        paddingInline: "20px",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          {value.type === "alert" ? (
            <Iconmoon icon="warning-icon" size={16} color="#EA655B" />
          ) : value.type === "event" ? (
            <Iconmoon icon="info_new" size={16} color="#0268D9" />
          ) : (
            <Iconmoon icon="bell" size={16} color="gray" />
          )}
          <p
            style={{
              marginLeft: "10px",
              fontWeight: 700,
              fontSize: "14px",
              color: "black",
              // display: "flex",
              // flexDirection: "column",
            }}
          >
            {`${value.title} :`}
            <span style={{fontWeight: 500}}>{ value.description}</span>
          </p>
        </div>

        <div style={{ display: "flex", alignItems: "center" }}>
          {!value.read_status ? (
            <Button
              className="textTypeBtn textBtnIcon"
              style={{ backgroundColor: "transparent",display:"flex",alignItems:"center",gap:"3px"}}
              onClick={() => markAsRead(value.id)}
            >
              <Iconmoon icon="check" size={16} color="#EA4E2C" />
              <span>Mark as read</span>
            </Button>
          ):<div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    columnGap: "3px",
                  }}
                >
                  <Iconmoon icon="seen" size={16} color="gray" />
                  <span
                    style={{
                      // fontSize: "12px",
                      fontWeight: "400",
                      color: "gray",
                    }}
                  >
                    Seen
                  </span>
                </div>}
          {value?.module_name != "System" && <Button className="themeBtn" onClick={() => handleView(value)}>
            <span style={{marginInline:"0px"}}>View</span>
          </Button>}
        </div>
      </div>
<div style={{width:"100%",display:"flex",justifyContent:"space-between",marginRight:"10px"}}>
      <div
        style={{
          marginTop: "6px",
          fontSize: "12px",
          color: "#605E5C",
          border: "1px solid #D2D0CE",
          borderRadius: "40px",
          // minWidth: "90px",
          // textAlign: "center",
          textTransform: "capitalize",
          backgroundColor: "#F3F2F1",
           padding: "5px 8px",
        }}
      >
        {value.sub_category === "order_approval_request"
          ? "Order Approval Request"
          : value.sub_category === "system"
          ? "System"
          : value.category}
      </div>
    

<JobListColumn style={{backgroundColor:"transparent",marginTop:"10px"}}>
  <span className="jbCardFooter jbdata" style={{display:"flex",justifyContent:"flex-end"}}>
    <span className="date">
      {new Date(value.read_status ? value.read_at : value.created_at).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "2-digit",
      })}
    </span>
    <span className="time">
      {new Date(value.read_status ? value.read_at : value.created_at).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })}
    </span>
  </span>
</JobListColumn>

</div>
    </div>
  );

  return (
    <Layout>
      <HeaderNav />
      <RightLayoutContainer>
        <OtherPages>
          <Layout style={{ backgroundColor: "#ffffff", paddingTop: "0px" }}>
            <PageTitleComp>
              <PageHeader
                ghost={false}
                title="Notifications"
                extra={[
                  <Button
                    key="1"
                    className="themeBtn noBg"
                    type="primary"
                    onClick={markAllAsRead}
                  >
                    <Iconmoon icon="check" size={16} color="#EA4E2C" />
                    Mark all as read
                  </Button>,
                ]}
              />
            </PageTitleComp>

            <ResultWrap1>
              <DetailFields>
                <Tabs
                  defaultActiveKey="1"
                  className="topPostTab"
                  onChange={(k) => setActiveTab(k)}
                >
                  {/* ✅ Alerts Tab */}
                  <TabPane tab="Alerts" key="1">
                    
                    {/* Unread Alerts Section */}
                    <div style={{ marginBottom: "20px" }}>
                      <h4 style={{ margin: "0 0 10px 0", color: "#333" }}>Unread Alerts</h4>
                      <InfiniteScroll
                        dataLength={unreadData.filter(n => n.type === "alert").length}
                        next={loadMoreUnread}
                        hasMore={hasMoreUnread}
                        loader={<h4>Loading...</h4>}
                        height={300}
                      >
                        {unreadData.filter(n => n.type === "alert").length > 0 ? (
                          unreadData.filter(n => n.type === "alert").map((it) => messageBox(it))
                        ) : (
                          <p style={{ textAlign: "center", color: "#999" }}>
                            No unread alerts.
                          </p>
                        )}
                      </InfiniteScroll>
                    </div>

                    {/* Read Alerts Section */}
                    <div>
                      <h4 style={{ margin: "0 0 10px 0", color: "#333" }}>Read Alerts</h4>
                      <InfiniteScroll
                        dataLength={earlierData.filter(n => n.type === "alert").length}
                        next={loadMoreEarlier}
                        hasMore={hasMoreEarlier}
                        loader={<h4>Loading...</h4>}
                        height={300}
                      >
                        {earlierData.filter(n => n.type === "alert").length > 0 ? (
                          earlierData.filter(n => n.type === "alert").map((it) => messageBox(it))
                        ) : !hasMoreEarlier ? (
                          <p style={{ textAlign: "center", color: "#999" }}>
                            No read alerts.
                          </p>
                        ) : null}
                      </InfiniteScroll>
                    </div>
                  </TabPane>

                  {/* ✅ Events Tab */}
                  <TabPane tab="Events" key="2">
                    
                    {/* Unread Events Section */}
                    <div style={{ marginBottom: "20px" }}>
                      <h4 style={{ margin: "0 0 10px 0", color: "#333" }}>Unread Events</h4>
                      <InfiniteScroll
                        dataLength={unreadData.filter(n => n.type === "event").length}
                        next={loadMoreUnread}
                        hasMore={hasMoreUnread}
                        loader={<h4>Loading...</h4>}
                        height={300}
                      >
                        {unreadData.filter(n => n.type === "event").length > 0 ? (
                          unreadData.filter(n => n.type === "event").map((it) => messageBox(it))
                        ) : (
                          <p style={{ textAlign: "center", color: "#999" }}>
                            No unread events.
                          </p>
                        )}
                      </InfiniteScroll>
                    </div>

                    {/* Read Events Section */}
                    <div>
                      <h4 style={{ margin: "0 0 10px 0", color: "#333" }}>Read Events</h4>
                      <InfiniteScroll
                        dataLength={earlierData.filter(n => n.type === "event").length}
                        next={loadMoreEarlier}
                        hasMore={hasMoreEarlier}
                        loader={<h4>Loading...</h4>}
                        height={300}
                      >
                        {earlierData.filter(n => n.type === "event").length > 0 ? (
                          earlierData.filter(n => n.type === "event").map((it) => messageBox(it))
                        ) : !hasMoreEarlier ? (
                          <p style={{ textAlign: "center", color: "#999" }}>
                            No read events.
                          </p>
                        ) : null}
                      </InfiniteScroll>
                    </div>
                  </TabPane>

                  {/* ✅ All Notifications */}
                  <TabPane tab="All Notifications" key="3">
                    
                    {/* Unread Notifications Section */}
                    <div style={{ marginBottom: "20px" }}>
                      <h4 style={{ margin: "0 0 10px 0", color: "#333" }}>Unread Notifications</h4>
                      <InfiniteScroll
                        dataLength={unreadData.length}
                        next={loadMoreUnread}
                        hasMore={hasMoreUnread}
                        loader={<h4>Loading...</h4>}
                        height={300}
                      >
                        {unreadData.length > 0 ? (
                          unreadData.map((it) => messageBox(it))
                        ) : (
                          <p style={{ textAlign: "center", color: "#999" }}>
                            No unread notifications.
                          </p>
                        )}
                      </InfiniteScroll>
                    </div>

                    {/* Read Notifications Section */}
                    <div>
                      <h4 style={{ margin: "0 0 10px 0", color: "#333" }}>Read Notifications</h4>
                      <InfiniteScroll
                        dataLength={earlierData.length}
                        next={loadMoreEarlier}
                        hasMore={hasMoreEarlier}
                        loader={<h4>Loading...</h4>}
                        height={300}
                      >
                        {earlierData.length > 0 ? (
                          earlierData.map((it) => messageBox(it))
                        ) : !hasMoreEarlier ? (
                          <p style={{ textAlign: "center", color: "#999" }}>
                            No read notifications.
                          </p>
                        ) : null}
                      </InfiniteScroll>
                    </div>
                  </TabPane>
                </Tabs>
              </DetailFields>
            </ResultWrap1>
          </Layout>
        </OtherPages>
      </RightLayoutContainer>
    </Layout>
  );
}
